import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, query } from '../api/client';
import type { AuditEntry, EmailSettingsResponse, OutboxEmail } from '../api/types';
import { IconAudit, IconInbox, IconSettings } from '../components/icons';
import { Pill } from '../components/license';
import {
  Button,
  Callout,
  Dialog,
  EmptyState,
  ErrorNote,
  SelectField,
  SkeletonRows,
  SecretReveal,
  TextField,
  useToast,
} from '../components/ui';
import { useCapabilities } from '../lib/auth';
import { formatDateTime, formatRelative, humaniseAction } from '../lib/license';
import { MetadataSummary } from './CompanyDetail';

/* ==========================================================================
   Activity log
   ========================================================================== */

const ACTOR_FILTERS = [
  { value: '', label: 'Everyone' },
  { value: 'staff', label: 'Our staff' },
  { value: 'user', label: 'Customers' },
] as const;

export function AuditLogPage() {
  const [actorType, setActorType] = useState<string>('');
  const [action, setAction] = useState('');

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['audit', 'all', actorType, action],
    queryFn: () =>
      api.get<{ entries: AuditEntry[]; nextBefore: number | null }>(
        `/api/staff/audit-logs${query({ actorType, action, limit: 100 })}`,
      ),
  });

  const entries = data?.entries ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Activity</h1>
          <p className="page-head__sub">
            Every change staff made, and every sign-in decision Heron returned — including the
            refusals.
          </p>
        </div>
      </div>

      <section className="panel">
        <div className="panel__head">
          <div className="filters grow">
            <div className="segmented" role="group" aria-label="Filter by who acted">
              {ACTOR_FILTERS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={actorType === option.value}
                  onClick={() => setActorType(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <input
              className="input"
              style={{ maxWidth: 240 }}
              placeholder="Action, e.g. license.extended"
              value={action}
              aria-label="Filter by action"
              onChange={(event) => setAction(event.currentTarget.value)}
            />
          </div>
        </div>

        {isPending && <SkeletonRows rows={8} />}
        {isError && (
          <div className="panel__body">
            <ErrorNote error={error} />
          </div>
        )}

        {!isPending && !isError && entries.length === 0 && (
          <EmptyState
            icon={<IconAudit size={30} />}
            title={action ? 'No entries match that action' : 'Nothing recorded yet'}
            body={
              action
                ? 'Action names look like license.extended, user.created or auth.login_failed.'
                : 'Every mutation and every login decision lands here as it happens.'
            }
            action={action ? <Button onClick={() => setAction('')}>Clear filter</Button> : undefined}
          />
        )}

        {entries.length > 0 && (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th>Who</th>
                  <th>Company</th>
                  <th>Details</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="cell-meta tabular nowrap" title={formatDateTime(entry.created_at)}>
                      {formatRelative(entry.created_at)}
                    </td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-strong">{humaniseAction(entry.action)}</span>
                        <code className="cell-meta">{entry.action}</code>
                      </div>
                    </td>
                    <td className="cell-meta">
                      {entry.actor_label ?? '—'}
                      <span className="faint"> ({entry.actor_type})</span>
                    </td>
                    <td className="cell-meta">
                      {entry.company_id && entry.company_name ? (
                        <Link to={`/companies/${entry.company_id}`}>{entry.company_name}</Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="cell-meta" style={{ maxWidth: 320 }}>
                      <MetadataSummary metadata={entry.metadata} />
                    </td>
                    <td className="cell-meta mono">{entry.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/* ==========================================================================
   Sent email
   ========================================================================== */

export function EmailOutboxPage() {
  const [status, setStatus] = useState('');
  const [reading, setReading] = useState<OutboxEmail | null>(null);
  const [configuring, setConfiguring] = useState(false);
  const { isAdmin } = useCapabilities();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['outbox', status],
    queryFn: () =>
      api.get<{ emails: OutboxEmail[] }>(
        `/api/staff/audit-logs/email-outbox${query({ status, limit: 60 })}`,
      ),
    refetchInterval: 30_000,
  });

  const emails = data?.emails ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Sent email</h1>
          <p className="page-head__sub">
            Every message Heron generated. When the customer says the invite never arrived, this is
            where you find out whether we sent it — and resend from their credential.
          </p>
        </div>
        {isAdmin && (
          <div className="page-head__actions">
            <Button icon={<IconSettings />} onClick={() => setConfiguring(true)}>
              Email settings
            </Button>
          </div>
        )}
      </div>

      {configuring && <EmailSettingsDialog onClose={() => setConfiguring(false)} />}

      <section className="panel">
        <div className="panel__head">
          <div className="segmented" role="group" aria-label="Filter by delivery status">
            {[
              { value: '', label: 'All' },
              { value: 'sent', label: 'Sent' },
              { value: 'failed', label: 'Failed' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={status === option.value}
                onClick={() => setStatus(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {isPending && <SkeletonRows rows={6} />}
        {isError && (
          <div className="panel__body">
            <ErrorNote error={error} />
          </div>
        )}

        {!isPending && !isError && emails.length === 0 && (
          <EmptyState
            icon={<IconInbox size={32} />}
            title="No email yet"
            body="Invites and password resets appear here the moment they are generated, whether or not the send succeeded."
          />
        )}

        {emails.length > 0 && (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>To</th>
                  <th>Subject</th>
                  <th>Kind</th>
                  <th>Status</th>
                  <th className="table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <tr key={email.id}>
                    <td className="cell-meta tabular nowrap" title={formatDateTime(email.created_at)}>
                      {formatRelative(email.created_at)}
                    </td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-strong">{email.to_email}</span>
                        {email.username && <span className="cell-meta mono">{email.username}</span>}
                      </div>
                    </td>
                    <td>{email.subject}</td>
                    <td className="cell-meta">{email.template}</td>
                    <td>
                      {email.status === 'sent' ? (
                        <Pill tone="live">Sent</Pill>
                      ) : email.status === 'failed' ? (
                        <Pill tone="expired">Failed</Pill>
                      ) : (
                        <Pill tone="expiring">Pending</Pill>
                      )}
                    </td>
                    <td className="table__actions">
                      <Button size="sm" onClick={() => setReading(email)}>
                        Read
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {reading && <EmailDialog email={reading} onClose={() => setReading(null)} />}
    </>
  );
}

function EmailDialog({ email, onClose }: { email: OutboxEmail; onClose: () => void }) {
  // Invite links live only in the message body. Surfacing it here is what makes
  // console-mode email usable — and is exactly why this page is staff-only.
  const link = /https?:\/\/\S*set-password\?token=\S+/.exec(email.body_text)?.[0];

  return (
    <Dialog open onClose={onClose} title={email.subject} wide>
      <div className="stack">
        <dl className="dl">
          <dt>To</dt>
          <dd>{email.to_email}</dd>
          <dt>Generated</dt>
          <dd className="tabular">{formatDateTime(email.created_at)}</dd>
          <dt>Status</dt>
          <dd>
            {email.status}
            {email.sent_at ? ` · ${formatDateTime(email.sent_at)}` : ''}
          </dd>
          {email.error ? (
            <>
              <dt>Error</dt>
              <dd className="danger-text">{email.error}</dd>
            </>
          ) : null}
        </dl>

        {link && <SecretReveal label="Set-password link" value={link} />}

        <div>
          <span className="field__label">Message</span>
          <pre
            className="secret__value scroll-area"
            style={{
              whiteSpace: 'pre-wrap',
              marginTop: 'var(--s2)',
              maxHeight: 320,
              overflowY: 'auto',
              lineHeight: 1.6,
            }}
          >
            {email.body_text}
          </pre>
        </div>

        <div className="form-actions">
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/* ==========================================================================
   Email settings (admin)
   ========================================================================== */

/** Common providers, so nobody has to look up a host and port. */
const SMTP_PRESETS = [
  { label: 'Gmail', host: 'smtp.gmail.com', port: 587, secure: false },
  { label: 'Outlook / Microsoft 365', host: 'smtp.office365.com', port: 587, secure: false },
] as const;

function EmailSettingsDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const { push } = useToast();

  const current = useQuery({
    queryKey: ['email-settings'],
    queryFn: () => api.get<EmailSettingsResponse>('/api/staff/email-settings'),
  });

  const [driver, setDriver] = useState<'console' | 'smtp' | 'resend'>('console');
  const [from, setFrom] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [secure, setSecure] = useState(false);
  const [user, setUser] = useState('');
  // Write-only, like every secret in Heron: blank keeps the saved one.
  const [password, setPassword] = useState('');
  const [resendKey, setResendKey] = useState('');
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    const s = current.data?.settings;
    if (!s) return;
    setDriver(s.driver);
    setFrom(s.from);
    setHost(s.smtpHost ?? '');
    setPort(String(s.smtpPort));
    setSecure(s.smtpSecure);
    setUser(s.smtpUser ?? '');
  }, [current.data]);

  const body = () => ({
    driver,
    from: from.trim(),
    smtpHost: host.trim() || null,
    smtpPort: Number(port) || 587,
    smtpSecure: secure,
    smtpUser: user.trim() || null,
    ...(password ? { smtpPassword: password } : {}),
    ...(resendKey ? { resendApiKey: resendKey } : {}),
  });

  const save = useMutation({
    mutationFn: () => api.put<EmailSettingsResponse>('/api/staff/email-settings', body()),
    onSuccess: (data) => {
      queryClient.setQueryData(['email-settings'], data);
      push({ tone: 'success', title: 'Email settings saved' });
      onClose();
    },
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<{ sent: boolean }>('/api/staff/email-settings/test', { ...body(), to: testTo.trim() }),
  });

  const passwordSet = current.data?.settings.smtpPasswordSet ?? false;
  const resendKeySet = current.data?.settings.resendApiKeySet ?? false;
  const smtp = driver === 'smtp';
  const resend = driver === 'resend';

  return (
    <Dialog open onClose={onClose} title="Email settings" hint="How Heron sends invites and password resets.">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        {current.isError && <ErrorNote error={current.error} />}
        {save.isError && <ErrorNote error={save.error} />}
        {current.data?.source === 'environment' && (
          <Callout tone="info">
            These come from the server&rsquo;s environment variables. Once you save here, these
            settings are used instead.
          </Callout>
        )}

        <SelectField
          label="Sending"
          value={driver}
          onChange={(e) => setDriver(e.currentTarget.value as 'console' | 'smtp' | 'resend')}
        >
          <option value="console">Don&rsquo;t send — keep emails on this page only</option>
          <option value="resend">Send with Resend</option>
          <option value="smtp">Send through a mail server (SMTP)</option>
        </SelectField>
        {smtp && (
          <Callout tone="warn">
            Railway blocks SMTP ports below its Pro plan, so a test from here times out. Use Resend
            there instead.
          </Callout>
        )}

        <TextField
          label="From"
          value={from}
          required
          placeholder="Heron License <no-reply@presoft.com.my>"
          hint="The sender people see. Most providers require this to be the SMTP account's own address."
          onChange={(e) => setFrom(e.currentTarget.value)}
        />

        {resend && (
          <TextField
            label="Resend API key"
            type="password"
            value={resendKey}
            autoComplete="new-password"
            mono
            placeholder={resendKeySet ? 'A key is saved — type to replace it' : 're_…'}
            hint="resend.com → API Keys → Create API key (Sending access). The From address must be on a domain verified in that Resend account. Stored encrypted; never shown again."
            onChange={(e) => setResendKey(e.currentTarget.value)}
          />
        )}

        {smtp && (
          <>
            <div className="row" style={{ gap: 'var(--s2)', flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="small muted">Fill in for:</span>
              {SMTP_PRESETS.map((p) => (
                <Button
                  key={p.label}
                  type="button"
                  size="sm"
                  onClick={() => {
                    setHost(p.host);
                    setPort(String(p.port));
                    setSecure(p.secure);
                  }}
                >
                  {p.label}
                </Button>
              ))}
            </div>

            <div className="form-grid form-grid--2">
              <TextField
                label="SMTP server"
                value={host}
                required
                mono
                placeholder="smtp.gmail.com"
                onChange={(e) => setHost(e.currentTarget.value)}
              />
              <TextField
                label="Port"
                type="number"
                value={port}
                required
                onChange={(e) => {
                  setPort(e.currentTarget.value);
                  // 465 is SSL from the first byte; 587 and 25 upgrade with STARTTLS.
                  if (e.currentTarget.value === '465') setSecure(true);
                  if (e.currentTarget.value === '587') setSecure(false);
                }}
              />
            </div>
            <SelectField
              label="Encryption"
              value={secure ? 'ssl' : 'starttls'}
              onChange={(e) => setSecure(e.currentTarget.value === 'ssl')}
            >
              <option value="starttls">STARTTLS (usually port 587)</option>
              <option value="ssl">SSL/TLS (usually port 465)</option>
            </SelectField>

            <TextField
              label="Username"
              value={user}
              autoComplete="off"
              placeholder="presoftsnow@gmail.com"
              onChange={(e) => setUser(e.currentTarget.value)}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              autoComplete="new-password"
              placeholder={passwordSet ? 'A password is saved — type to replace it' : ''}
              hint="For Gmail, an App password (Google Account → Security → App passwords), not the normal one. Stored encrypted; never shown again."
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
          </>
        )}

        {(smtp || resend) && (
          <div className="subsection">
            <div className="stack stack--tight">
              <strong className="subsection__title">Send a test email</strong>
              <div className="row" style={{ gap: 'var(--s2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 16rem' }}>
                  <TextField
                    label="To"
                    type="email"
                    value={testTo}
                    placeholder="you@presoft.com.my"
                    onChange={(e) => setTestTo(e.currentTarget.value)}
                  />
                </div>
                <Button
                  type="button"
                  loading={test.isPending}
                  disabled={
                    !testTo.trim() || (smtp ? !host.trim() : !resendKey && !resendKeySet)
                  }
                  onClick={() => test.mutate()}
                >
                  Send test
                </Button>
              </div>
              {test.isError && <ErrorNote error={test.error} />}
              {test.isSuccess && (
                <span className="small success-text">Sent. Check the inbox (and spam) of {testTo}.</span>
              )}
            </div>
          </div>
        )}

        <div className="form-actions">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={current.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
